import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { IconArrowDown, Logo } from "assets/svg";
import { Input, Select } from "antd";
import { INDUSTRY_OPTIONS } from "common/enums/Industry.enum";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserTypeEnum } from "common/enums/UserType.enum";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { setRegistrationFormData } from "reducers/informationSlice";

interface InformationFormData {
  company_name: string;
  postal_code: string;
  prefecture: string;
  address: string;
  building_room: string;
  industry: string;
  contact_last_name: string;
  contact_first_name: string;
  phone_number: string;
  password: string;
  password_confirmation: string;
  number_card_url?: string;
  corp_regist_cert_url?: string;
  type?: number;
}

const Information: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { registrationFormData } = useSelector(
    (state: RootState) => state.information,
  );
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<InformationFormData>({
    defaultValues: registrationFormData || {},
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Load existing form data when component mounts
  useEffect(() => {
    if (registrationFormData) {
      reset(registrationFormData);
    }
  }, [registrationFormData, reset]);

  const onSubmit = (data: InformationFormData) => {
    const token = searchParams.get("token");
    const type = searchParams.get("type");

    dispatch(setRegistrationFormData(data));

    if (Number(type) === UserTypeEnum.COMPANY_SELLER) {
      navigate(`/verify-information?token=${token}&type=${type}`);
    } else {
      navigate(`/accept-term-service?token=${token}&type=${type}`);
    }
  };

  return (
    <main id="information-page" className="bg--blur box">
      <div
        className="w-full relative rounded-[20px] border-[8px] border-white bg-primary-light shadow-[0_0_15px_0_rgba(0,0,0,0.05)] flex flex-col items-center justify-center py-[52px] px-[22px]
        sm:max-w-[1020px] sm:py-[72px] sm:px-[62px]"
      >
        <Logo
          className="flex justify-center items-center w-[200px] h-[81px] aspect-[1/1] mb-[30px]
             sm:w-[300px] sm:h-[121px] sm:mb-[40px]"
        />
        <h2
          className="text-[22px] not-italic font-bold text-primary mb-[15px]
        sm:text-[28px]"
        >
          企業情報の入力
        </h2>
        <p className="text-[15px] sm:text-[16px] leading-[160%] text-center mb-[30px] sm:mb-[40px]">
          以下の情報を入力して <br className="sm:hidden" />
          本登録を続けてください。
        </p>

        <form className="w-full" onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* 企業情報 Section */}
          <div className="mb-[30px] sm:mb-[40px]">
            <h3 className="text-[18px] py-[8px] sm:py-[10px] font-bold text-primary mb-[30px] sm:text-[20px] sm:mb-[40px] flex border-b border-b-neutral-border">
              <div className="w-[4px] h-[29px] bg-primary inline-block mr-[10px] sm:mr-[15px] rounded-[6px]"></div>
              企業情報
            </h3>

            <div className="flex flex-col gap-[25px] sm:gap-[20px] mb-[30px] md:mb-[50px]">
              {/* 法人名 */}
              <div className="sm:flex sm:flex-row sm:justify-between">
                <div className="flex items-center text-[15px] mb-[8px] sm:mb-0 sm:text-[16px] mr-[20px] sm:w-[200px] text-black">
                  <span className="bg-red-dark px-[10px] md:px-[15px] text-white text-[11px] rounded-[6px] mr-[10px]">
                    必須
                  </span>
                  法人名
                </div>
                <Controller
                  name="company_name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="◯◯産業株式会社"
                      className="w-full sm:w-[calc(100%-220px)] h-[50px] px-[20px] rounded-[6px] text-[16px] placeholder-gray-400 sm:h-[70px] sm:px-[20px] sm:text-[18px] border-none"
                      status={errors.company_name ? "error" : ""}
                    />
                  )}
                />
              </div>

              {/* 郵便番号 */}
              <div className="sm:flex sm:flex-row">
                <div className="flex items-center text-[15px] mb-[8px] sm:mb-0 sm:text-[16px] mr-[20px] sm:w-[200px] text-black">
                  <span className="bg-red-dark px-[10px] md:px-[15px] text-white text-[11px] rounded-[6px] mr-[10px]">
                    必須
                  </span>
                  郵便番号
                </div>
                <Controller
                  name="postal_code"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="123-4567"
                      className="w-1/2 sm:w-[200px] h-[50px] px-[20px] rounded-[6px] text-[16px] placeholder-gray-400 sm:h-[70px] sm:px-[20px] sm:text-[18px] border-none"
                      status={errors.postal_code ? "error" : ""}
                    />
                  )}
                />
              </div>

              {/*都道府県*/}
              <div className="sm:flex sm:flex-row sm:justify-between">
                <div className="flex items-center text-[15px] mb-[8px] sm:mb-0 sm:text-[16px] mr-[20px] sm:w-[200px] text-black">
                  <span className="bg-red-dark px-[10px] md:px-[15px] text-white text-[11px] rounded-[6px] mr-[10px]">
                    必須
                  </span>
                  都道府県
                </div>
                <Controller
                  name="prefecture"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className="w-full sm:w-[calc(100%-220px)] h-[50px] px-[20px] rounded-[6px] text-[16px] placeholder-gray-400 sm:h-[70px] sm:px-[20px] sm:text-[18px] border-none"
                      placeholder="東京都"
                      status={errors.prefecture ? "error" : ""}
                    />
                  )}
                />
              </div>

              {/*市区町村・番地*/}
              <div className="sm:flex sm:flex-row sm:justify-between">
                <div className="flex items-center text-[15px] mb-[8px] sm:mb-0 sm:text-[16px] mr-[20px] sm:w-[200px] text-black">
                  <span className="bg-red-dark px-[10px] md:px-[15px] text-white text-[11px] rounded-[6px] mr-[10px]">
                    必須
                  </span>
                  市区町村・番地
                </div>
                <Controller
                  name="address"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className="w-full sm:w-[calc(100%-220px)] h-[50px] px-[20px] rounded-[6px] text-[16px] placeholder-gray-400 sm:h-[70px] sm:px-[20px] sm:text-[18px] border-none"
                      placeholder="〇〇区〇〇町1-2-3"
                      status={errors.address ? "error" : ""}
                    />
                  )}
                />
              </div>

              {/*建物名・部屋番号 (任意)*/}
              <div className="sm:flex sm:flex-row sm:justify-between">
                <div className="flex items-center text-[15px] mb-[8px] sm:mb-0 sm:text-[16px] mr-[20px] sm:w-[200px] text-black">
                  <span className="bg-[#dfebf4] px-[10px] md:px-[15px] text-[11px] rounded-[6px] mr-[10px] text-primary">
                    任意
                  </span>
                  建物名・部屋番号
                </div>
                <Controller
                  name="building_room"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className="w-full sm:w-[calc(100%-220px)] h-[50px] px-[20px] rounded-[6px] text-[16px] placeholder-gray-400 sm:h-[70px] sm:px-[20px] sm:text-[18px] border-none"
                      placeholder="〇〇マンション 305"
                    />
                  )}
                />
              </div>

              {/* 業種 */}
              <div className="sm:flex sm:flex-row sm:justify-between">
                <div className="flex items-center text-[15px] mb-[8px] sm:mb-0 sm:text-[16px] mr-[20px] sm:w-[200px] text-black">
                  <span className="bg-red-dark px-[10px] md:px-[15px] text-white text-[11px] rounded-[6px] mr-[10px]">
                    必須
                  </span>
                  業種
                </div>
                <Controller
                  name="industry"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      suffixIcon={<IconArrowDown />}
                      placeholder="選択してください"
                      className={`w-full sm:w-[calc(100%-220px)] h-[50px] rounded-[6px] placeholder-gray-400 sm:h-[70px] placeholder-size ${errors.industry ? "error" : ""}`}
                      options={Object.values(INDUSTRY_OPTIONS)}
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                    />
                  )}
                />
              </div>

              {/*担当者名*/}
              <div className="sm:flex sm:flex-row sm:justify-between">
                <div className="flex items-center text-[15px] mb-[8px] sm:mb-0 sm:text-[16px] mr-[20px] sm:w-[200px] text-black">
                  <span className="bg-red-dark px-[10px] md:px-[15px] text-white text-[11px] rounded-[6px] mr-[10px]">
                    必須
                  </span>
                  担当者名
                </div>
                <div className="w-full sm:w-[calc(100%-220px)] flex flex-col md:flex-row gap-[10px] md:gap-[15px]">
                  <Controller
                    name="contact_last_name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        className="h-[50px] px-[20px] rounded-[6px] text-[16px] placeholder-gray-400 sm:h-[70px] sm:px-[20px] sm:text-[18px] border-none"
                        placeholder="山田"
                        status={errors.contact_last_name ? "error" : ""}
                      />
                    )}
                  />
                  <Controller
                    name="contact_first_name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        className="h-[50px] px-[20px] rounded-[6px] text-[16px] placeholder-gray-400 sm:h-[70px] sm:px-[20px] sm:text-[18px] border-none"
                        placeholder="太郎"
                        status={errors.contact_first_name ? "error" : ""}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="sm:flex sm:flex-row sm:justify-between">
                <div className="flex items-center text-[15px] mb-[8px] sm:mb-0 sm:text-[16px] mr-[20px] sm:w-[200px] text-black">
                  <span className="bg-red-dark px-[10px] md:px-[15px] text-white text-[11px] rounded-[6px] mr-[10px]">
                    必須
                  </span>
                  電話番号
                </div>
                <Controller
                  name="phone_number"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className="w-full sm:w-[calc(100%-220px)] h-[50px] px-[20px] rounded-[6px] text-[16px] placeholder-gray-400 sm:h-[70px] sm:px-[20px] sm:text-[18px] border-none"
                      placeholder="01-2345-6789"
                      status={errors.phone_number ? "error" : ""}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="mb-[30px] sm:mb-[40px]">
            <h3 className="text-[18px] py-[8px] sm:py-[10px] font-bold text-primary mb-[30px] sm:text-[20px] sm:mb-[40px] flex border-b border-b-neutral-border">
              <div className="w-[4px] h-[29px] bg-primary inline-block mr-[10px] sm:mr-[15px] rounded-[6px]"></div>
              ログイン情報
            </h3>
            <div className="flex flex-col gap-[25px] sm:gap-[20px] mb-[30px] md:mb-[50px]">
              {/* パスワード */}
              <div
                className={`sm:flex sm:flex-row sm:justify-between  ${errors.password_confirmation ? "mb-[10px]" : ""}`}
              >
                <div className="flex items-center text-[15px] mb-[8px] sm:mb-0 sm:text-[16px] mr-[20px] sm:w-[200px] text-black">
                  <span className="bg-red-dark px-[10px] md:px-[15px] text-white text-[11px] rounded-[6px] mr-[10px]">
                    必須
                  </span>
                  パスワード
                </div>
                <div className="w-full sm:w-[calc(100%-220px)] h-[50px] rounded-[6px] text-[16px] placeholder-gray-400 sm:h-[70px] sm:text-[18px]">
                  <Controller
                    name="password"
                    control={control}
                    rules={{ required: true, minLength: 8 }}
                    render={({ field }) => (
                      <Input.Password
                        {...field}
                        placeholder="8文字以上"
                        className="h-[100%] border-none"
                        status={errors.password ? "error" : ""}
                      />
                    )}
                  />
                  {errors.password?.type === "minLength" && (
                    <span className="text-red-500 text-[12px] ml-2 mb-2">
                      8文字以上のパスワードを入力してください。
                    </span>
                  )}
                </div>
              </div>

              {/* パスワード（確認） */}
              <div className="sm:flex sm:flex-row sm:justify-between">
                <div className="flex items-center text-[15px] mb-[8px] sm:mb-0 sm:text-[16px] mr-[20px] sm:w-[200px] text-black  sm:justify-between">
                  <span className="bg-red-dark px-[10px] md:px-[15px] text-white text-[11px] rounded-[6px]">
                    必須
                  </span>
                  パスワード（確認）
                </div>
                <div className="w-full sm:w-[calc(100%-220px)] h-[50px] rounded-[6px] text-[16px] placeholder-gray-400 sm:h-[70px] sm:text-[18px]">
                  <Controller
                    name="password_confirmation"
                    control={control}
                    rules={{
                      required: true,
                      minLength: 8,
                      validate: (value) =>
                        value === control._formValues.password ||
                        "パスワードが一致しません",
                    }}
                    render={({ field }) => (
                      <Input.Password
                        {...field}
                        placeholder="8文字以上"
                        className="h-[100%] border-none"
                        status={errors.password_confirmation ? "error" : ""}
                      />
                    )}
                  />
                  {errors.password_confirmation?.type === "minLength" && (
                    <span className="text-red-500 text-[12px] ml-2">
                      8文字以上のパスワードを入力してください。
                    </span>
                  )}
                  {errors.password_confirmation?.type === "validate" && (
                    <span className="text-red-500 text-[12px] ml-2">
                      {errors.password_confirmation.message}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-primary text-white py-[20px] rounded-[6px] text-[16px] font-bold  mb-[15px] sm:mb-[20px] sm:text-[18px] sm:max-w-[340px] sm:mx-auto sm:block"
          >
            利用規約を確認する
          </button>

          <p className="text-[12px] sm:text-[13px] text-center">
            ※この後、本人確認書類の提出が必要です。
          </p>
        </form>
      </div>
    </main>
  );
};

export default Information;
